import { alpha } from '@mui/material/styles';
import {
  Box,
  Avatar,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
} from '@mui/material';
import Iconify from 'src/minimals/components/Iconify';
import SearchNotFound from './SearchNotFound';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getContacts } from 'src/redux/thunks/conversation';
import { useTranslation } from 'react-i18next';

const ContactsAutocomplete = ({ contacts, recipients, onAddRecipients }) => {
  const workspaceId = useSelector((state) => state.user.currentWorkspace);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const handleAddRecipients = (recipients) => {
    setQuery('');
    onAddRecipients(recipients);
  };

  useEffect(() => {
    const loadContacts = async () => {
      try {
        await dispatch(getContacts(workspaceId)).unwrap();
      } catch (error) {
        console.error('Error loading contacts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContacts();
  }, [workspaceId, dispatch]);

  return (
    <Autocomplete
      multiple
      size="small"
      disablePortal
      popupIcon={null}
      noOptionsText={<SearchNotFound searchQuery={query} />}
      value={recipients}
      onChange={(event, value) => handleAddRecipients(value)}
      onInputChange={(event, value) => setQuery(value)}
      options={contacts}
      getOptionLabel={(recipient) => recipient.name}
      renderOption={(props, recipient, { inputValue, selected }) => {
        const { name, profilePicture } = recipient;
        return (
          <Box component="li" sx={{ p: '12px !important' }} {...props}>
            <Box
              sx={{
                mr: 1.5,
                position: 'relative',
                width: 32,
                height: 32,
                overflow: 'hidden',
                borderRadius: '100%',
              }}
            >
              <Avatar
                alt={name}
                src={profilePicture}
                sx={{
                  width: 32,
                  height: 32,
                }}
              />
              <Box
                sx={{
                  top: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  position: 'absolute',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
                  transition: (theme) =>
                    theme.transitions.create('opacity', {
                      easing: theme.transitions.easing.easeInOut,
                      duration: theme.transitions.duration.shorter,
                    }),
                  ...(selected && {
                    opacity: 1,
                    color: 'primary.main',
                  }),
                }}
              >
                <Iconify icon="eva:checkmark-fill" width={20} height={20} />
              </Box>
            </Box>
            <Typography variant="subtitle2" color="textPrimary">
              {name}
            </Typography>
          </Box>
        );
      }}
      renderTags={(recipients, getTagProps) =>
        recipients.map((recipient, index) => {
          const { id, name, avatar } = recipient;
          return (
            <Chip
              {...getTagProps({ index })}
              key={id}
              size="small"
              label={name}
              color="info"
              avatar={<Avatar alt={name} src={avatar} />}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          placeholder={
            recipients.length === 0 ? t('chat.recipients_placeholder_text') : ''
          }
        />
      )}
      disabled={loading}
    />
  );
};

export default ContactsAutocomplete;
